import React, { useEffect, useMemo, useState, useRef, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Check, ChevronsUpDown, Search } from "lucide-react";
import { InformationCircleIcon, PlusIcon } from "@heroicons/react/24/outline";
import { Popover, PopoverContent, PopoverTrigger } from "../../components/shadcn/popover";
import { cn } from "../../lib/utils";
import { search } from "../../api/ProjectApi";
import { fromJS, isImmutable } from "immutable";
import { navigateToItem, showCreateItemDialog } from "../../Navigator";
import { BasePickerProps, LinkItemOption, renderItemIcon, renderAvatar } from "./picker-shared";
import { PersonIcon } from "../../icons/fieldTypeIcons";

export interface ContactPickerProps extends BasePickerProps {
  type: "CONTACT";
}

const ContactPicker: React.FC<ContactPickerProps> = ({
  onChange = () => {},
  type = "CONTACT",
  items,
  projectId = "all",
  placeholder = "Select a contact",
  autoOpen = false,
  className = "",
  noBorder = true,
  showBorder,
  maxWidth,
  width,
  popoverMinWidth = "282px",
  showInfoIcon = true,
  showSelectIcon = false,
  hideSelectIconUntilHover = false,
  showIcon = false,
  isViewMode = false,
  projectView = "LIST",
  customTrigger,
  showTriggerHeight = false,
  useInApp = true,
}) => {
  // Computed values
  const componentWidth = width || maxWidth || "320px";
  const shouldShowBorder = showBorder !== undefined ? showBorder : !noBorder;

  // Redux and navigation
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const project = useSelector((state: any) => state.projectReducer.get("project"));

  // State
  const [open, setOpen] = useState<boolean>(false);
  const [searchText, setSearchText] = useState("");
  const [options, setOptions] = useState<LinkItemOption[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [hasLoadedInitial, setHasLoadedInitial] = useState<boolean>(false);
  const [triggerHeight, setTriggerHeight] = useState<number>(customTrigger ? 20 : 36);
  const [triggerWidth, setTriggerWidth] = useState<number | null>(null);

  // Refs
  const inputRef = useRef<HTMLInputElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const customTriggerRef = useRef<HTMLElement | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const popoverContentRef = useRef<HTMLDivElement>(null);

  const currentValue = useMemo<LinkItemOption | null>(() => {
    if (!items) return null;
    const data: any = isImmutable(items) ? items.toJS() : items;
    if (!data || data.value === "" || data.value === null) {
      return null;
    }
    return {
      value: data.value || "",
      label: data.label || "",
      description: data.description || "",
      iconUrl: data.iconUrl || "",
      type: data.type || type,
      id: data.id || data.value || "",
      projectId: data.projectId,
    };
  }, [items, type]);

  const searchItems = useCallback(
    async (query: string): Promise<LinkItemOption[]> => {
      try {
        const searchFilters = query?.trim()
          ? {
              name: {
                operator: "regex" as const,
                values: [query],
              },
            }
          : {};

        const data = await search({
          projectId,
          pageSize: 10,
          types: [type],
          filters: searchFilters,
        });

        const datas = fromJS(data?.pageResult?.content || []);
        return datas
          .map((item: any) => ({
            label: item.getIn(["data", "name", "value"]),
            value: item.get("id"),
            description: item.getIn(["data", "email", "value"]),
            id: item.get("id"),
            type: item.get("type"),
            iconUrl: item.get("iconUrl"),
            projectId: item.get("projectId"),
          }))
          .toJS();
      } catch (error) {
        console.error("Error searching contacts:", error);
        return [];
      }
    },
    [projectId, type]
  );

  const performSearch = useCallback(
    async (query: string) => {
      if (isViewMode) {
        setOptions([]);
        return;
      }
      setLoading(true);
      try {
        const results = await searchItems(query);
        setOptions(results);
      } catch (error) {
        console.error("Error in performSearch:", error);
        setOptions([]);
      } finally {
        setLoading(false);
      }
    },
    [type, isViewMode, searchItems] // eslint-disable-line react-hooks/exhaustive-deps
  );

  useEffect(() => {
    setOpen(!!autoOpen);
  }, [autoOpen]);

  useEffect(() => {
    if (open && !hasLoadedInitial && !loading) {
      setHasLoadedInitial(true);
      performSearch("");
    }
    if (!open) {
      setHasLoadedInitial(false);
      setOptions([]);
    }
  }, [open, hasLoadedInitial, loading, performSearch]);

  useEffect(() => {
    if (!open || !hasLoadedInitial) return;

    const timer = setTimeout(() => {
      performSearch(searchText);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchText, open, hasLoadedInitial, performSearch]);

  useEffect(() => {
    if (open && inputRef.current) {
      const timeoutId = setTimeout(() => {
        inputRef.current?.focus();
      }, 0);
      return () => clearTimeout(timeoutId);
    }
    if (!open) {
      setSearchText("");
    }
  }, [open]);

  // Calculate trigger dimensions when popover opens
  useEffect(() => {
    if (!open) return;

    const rafId = requestAnimationFrame(() => {
      if (customTrigger) {
        const containerWidth = containerRef.current?.offsetWidth;
        if (containerWidth) {
          setTriggerWidth(containerWidth);
        }
        if (customTriggerRef.current) {
          setTriggerHeight(customTriggerRef.current.offsetHeight);
        }
      } else if (triggerRef.current) {
        setTriggerHeight(triggerRef.current.offsetHeight);
        setTriggerWidth(triggerRef.current.offsetWidth);
      }
    });

    return () => cancelAnimationFrame(rafId);
  }, [open, customTrigger]);

  const filteredOptions = useMemo(() => {
    if (!searchText) return options;
    const searchLower = searchText.toLowerCase();
    return options.filter(
      (option) =>
        option.label.toLowerCase().includes(searchLower) ||
        option.description?.toLowerCase().includes(searchLower)
    );
  }, [options, searchText]);

  const handleSelect = (option: LinkItemOption) => {
    onChange(option);
    setOpen(false);
    setSearchText("");
  };

  const handleCreate = () => {
    showCreateItemDialog({
      dispatch,
      type: "CONTACT",
      item: {
        projectId: project.get("id"),
        type: "CONTACT",
        projectView,
      },
      onClose: () => {
        performSearch(searchText);
      },
      useInApp,
    });
  };

  const handleInfoClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!currentValue) return;

    const item = fromJS({
      id: currentValue.value,
      type,
      projectId: project.get("id"),
    });
    navigateToItem({ item, navigate });
  };

  if (isViewMode) {
    const hasValue = currentValue != null && currentValue.value != "";
    return (
      <div
        className={cn(
          "w-full cursor-pointer flex items-center justify-between h-9 rounded-sm focus:outline-none focus:ring-0",
          shouldShowBorder && "border border-gray-300",
          className
        )}
        style={componentWidth ? { width: componentWidth, maxWidth: componentWidth } : {}}
      >
        <div className="flex items-center flex-1 min-w-0 overflow-hidden px-2">
          <div className="flex items-center min-w-0 flex-1">
            {showIcon && hasValue && (
              <div className="shrink-0 mr-1">
                {currentValue && renderAvatar(currentValue, type, "w-[26px] h-[26px]")}
              </div>
            )}
            {!showIcon && currentValue && renderItemIcon(currentValue, type, "w-[26px] h-[26px]")}
            <span
              className={cn(
                "text-[12px] font-medium leading-[16px] truncate flex-1 min-w-0 text-left",
                hasValue ? "text-[#1f2937]" : "text-gray-500"
              )}
            >
              {hasValue ? currentValue.label || "No record" : "Select a value"}
            </span>
          </div>
        </div>
        {showSelectIcon && (
          <div
            className={`shrink-0 px-2 pointer-events-none flex items-center transition-opacity duration-150 ${hideSelectIconUntilHover ? "opacity-0 group-hover/cell:opacity-100" : ""}`}
          >
            <ChevronsUpDown className="w-3.5 h-3.5 text-gray-500" />
          </div>
        )}
      </div>
    );
  }

  // Helper: Get popover content width style
  const getPopoverWidthStyle = (): React.CSSProperties => {
    if (customTrigger && triggerWidth) {
      return { width: `${triggerWidth}px`, minWidth: popoverMinWidth };
    }
    if (customTrigger) {
      return { width: componentWidth || "100%", minWidth: popoverMinWidth };
    }
    if (componentWidth) {
      const width =
        componentWidth === "100%" ? "var(--radix-popover-trigger-width)" : componentWidth;
      return { width, minWidth: popoverMinWidth };
    }
    return { width: "var(--radix-popover-trigger-width)", minWidth: popoverMinWidth };
  };

  // Helper: Forward ref for custom trigger element
  const forwardCustomTriggerRef = (node: HTMLElement | null) => {
    customTriggerRef.current = node;
    const originalRef = (customTrigger as any).ref;
    if (originalRef) {
      if (typeof originalRef === "function") {
        originalRef(node);
      } else {
        originalRef.current = node;
      }
    }
  };

  const hasValue = currentValue != null && currentValue.value != "";

  return (
    <div
      ref={containerRef}
      className={cn("w-full", className)}
      style={componentWidth ? { width: componentWidth, maxWidth: componentWidth } : {}}
    >
      <Popover open={open} modal={false} onOpenChange={setOpen}>
        {customTrigger ? (
          <PopoverTrigger asChild>
            {React.isValidElement(customTrigger) ? (
              React.cloneElement(customTrigger as any, {
                ref: forwardCustomTriggerRef,
                onClick: (e: React.MouseEvent) => {
                  setOpen(true);
                  (customTrigger as any).props?.onClick?.(e);
                },
                className: cn("cursor-pointer", (customTrigger as any).props?.className),
              })
            ) : (
              <div
                ref={(node) => {
                  customTriggerRef.current = node;
                }}
                onClick={() => setOpen(true)}
                className="cursor-pointer"
              >
                {customTrigger}
              </div>
            )}
          </PopoverTrigger>
        ) : (
          <PopoverTrigger asChild>
            <button
              ref={triggerRef}
              type="button"
              className={cn(
                "flex h-9 w-full items-center justify-between rounded-sm bg-background px-2 py-1.5 text-xs placeholder:text-muted-foreground focus:outline-none disabled:cursor-not-allowed disabled:opacity-50",
                shouldShowBorder && "border border-gray-300",
                !shouldShowBorder && "border-0 focus:border-0 active:border-0",
                "focus:ring-0 focus-visible:ring-0 focus-visible:ring-offset-0",
                !hasValue && "text-gray-500"
              )}
              style={componentWidth ? { width: componentWidth, minWidth: componentWidth } : {}}
              onClick={() => setOpen(true)}
            >
              <div className="flex items-center gap-2 flex-1 min-w-0">
                {hasValue ? (
                  <>
                    {showIcon && (
                      <div className="shrink-0">
                        {renderAvatar(currentValue, type, "w-[20px] h-[20px] text-[10px]")}
                      </div>
                    )}
                    {!showIcon && renderItemIcon(currentValue, type, "w-[20px] h-[20px]")}
                    <span className="text-[12px] font-medium leading-[16px] truncate flex-1 min-w-0 text-left text-[#1f2937]">
                      {currentValue.label}
                    </span>
                    {showInfoIcon && (
                      <InformationCircleIcon
                        className="w-4 h-4 text-gray-400 hover:text-blue-600 cursor-pointer shrink-0"
                        onClick={handleInfoClick}
                      />
                    )}
                  </>
                ) : (
                  <>
                    {showIcon !== false && (
                      <PersonIcon
                        width={16}
                        height={16}
                        viewBox="0 0 16 16"
                        className="w-[20px] h-[20px] text-gray-500 shrink-0 opacity-80"
                      />
                    )}
                    <span className="text-gray-500 text-sm">{placeholder}</span>
                  </>
                )}
              </div>
              {showSelectIcon && (
                <div
                  className={`flex items-center ml-2 shrink-0 transition-opacity duration-150 ${hideSelectIconUntilHover ? "opacity-0 group-hover/cell:opacity-100" : ""}`}
                >
                  <ChevronsUpDown className="h-3.5 w-3.5 text-gray-400" />
                </div>
              )}
            </button>
          </PopoverTrigger>
        )}
        <PopoverContent
          ref={popoverContentRef}
          className={cn("p-0 bg-white border border-gray-200 shadow-lg transition-none")}
          style={{
            ...getPopoverWidthStyle(),
            zIndex: 100,
            ...(showTriggerHeight && {
              marginTop: `-${triggerHeight}px`,
            }),
          }}
          onOpenAutoFocus={() => {
            if (customTrigger && !triggerWidth && containerRef.current) {
              const containerWidth = containerRef.current.offsetWidth;
              if (containerWidth) {
                setTriggerWidth(containerWidth);
              }
            }
          }}
          align="start"
          side="bottom"
          sideOffset={0}
          onClick={(e) => {
            // Prevent event from bubbling up and closing the popover
            e.stopPropagation();
          }}
          onPointerDownOutside={(e) => {
            const target = e.target as HTMLElement;
            // Prevent closing when clicking inside the popover content
            if (popoverContentRef.current && popoverContentRef.current.contains(target)) {
              e.preventDefault();
            }
          }}
          onFocusOutside={(e) => {
            const target = e.target as HTMLElement;
            // Prevent closing when focusing inside the popover content
            if (popoverContentRef.current && popoverContentRef.current.contains(target)) {
              e.preventDefault();
            }
          }}
          onInteractOutside={(e) => {
            const target = e.target as HTMLElement;
            // Prevent closing when interacting inside the popover content
            if (popoverContentRef.current && popoverContentRef.current.contains(target)) {
              e.preventDefault();
            }
          }}
        >
          <div
            onClick={(e) => {
              // Prevent event from bubbling up and closing the popover
              e.stopPropagation();
            }}
            onMouseDown={(e) => {
              // Prevent event from bubbling up and closing the popover
              e.stopPropagation();
            }}
          >
            <div className="px-2 py-1.5 border-b border-gray-200">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                <input
                  ref={inputRef}
                  type="text"
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  placeholder="Search or create contact..."
                  className="w-full pl-8 pr-2 h-7 text-sm bg-transparent border-0 focus:outline-none focus:ring-0 placeholder:text-gray-400"
                />
              </div>
            </div>

            <div className="py-1 max-h-64 overflow-y-auto">
              {loading ? (
                <div className="px-3 py-2 text-sm text-gray-500">Loading...</div>
              ) : filteredOptions.length === 0 ? (
                <div className="px-3 py-2 text-sm text-gray-500">No records</div>
              ) : (
                filteredOptions.map((option) => {
                  const isSelected = currentValue?.value === option.value;
                  return (
                    <button
                      key={option.value}
                      type="button"
                      onPointerDownCapture={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                      }}
                      onMouseDownCapture={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                      }}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleSelect(option);
                      }}
                      className={cn(
                        "w-full flex items-center justify-between px-3 py-2 text-sm hover:bg-gray-100 transition-colors",
                        isSelected && "bg-blue-50"
                      )}
                    >
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        {showIcon ? (
                          <div className="shrink-0">{renderAvatar(option, type, "w-5 h-5")}</div>
                        ) : (
                          renderItemIcon(option, type, "w-5 h-5")
                        )}
                        <div className="flex flex-col flex-1 min-w-0">
                          <span className="text-sm text-left truncate">{option.label}</span>
                          {option.description && (
                            <span className="text-xs text-gray-500 truncate text-left">
                              {option.description}
                            </span>
                          )}
                        </div>
                      </div>
                      {isSelected && <Check className="w-4 h-4 text-blue-600 shrink-0 ml-2" />}
                    </button>
                  );
                })
              )}
            </div>

            <div className="border-t border-gray-200 p-2">
              <button
                type="button"
                onPointerDownCapture={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleCreate();
                }}
                onMouseDownCapture={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
              >
                <PlusIcon className="w-4 h-4 text-gray-500" />
                <span>Create new Contact</span>
              </button>
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
};

export default ContactPicker;
